import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Ruler, 
  Box, 
  Settings, 
  Calculator as CalculatorIcon, 
  Flame, 
  Building, 
  MoveHorizontal, 
  MoveVertical, 
  Layers, 
  RefreshCw,
  ArrowRight,
  Check,
  Delete,
  ShieldAlert,
  Trash,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';

// UI Bileşenleri
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';

import { Switch } from './ui/switch';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Skeleton } from './ui/skeleton';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { cn } from '../lib/utils';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { AutocompleteInput } from './ui/autocomplete-input';

// Custom hooks
import useFieldSuggestions from '../lib/hooks/useFieldSuggestions';

// Veri ve algoritma (sadeleştirildi)
import productData, { getProductById, getExtinguisherProducts, getProductName, getProductDisplayName } from '../lib/productData';
import { getAllParameters, getParameterByCode, getDensityByCode } from '../lib/parameterData';
import { calculateWithAlasAlgorithm } from '../lib/alasAlgorithm';

// Backend tamamen kaldırıldı - Supabase ve client-side hesaplama kullanılıyor

/**
 * Hesaplama formu bileşeni
 * @param {Object} props - Bileşen özellikleri
 * @param {Function} props.onCalculationResults - Hesaplama sonuçlarını işleyen fonksiyon
 * @param {Function} props.onError - Hata mesajlarını işleyen fonksiyon
 * @returns {JSX.Element} - Hesaplama formu bileşeni
 */
const CalculatorForm = ({ onCalculationResults, onError }) => {
  const { t, i18n } = useTranslation();
  
  // Kullanıcı ID'sini alasfireSession'dan al
  let dealerId = 'anonymous';
  const sessionData = localStorage.getItem('alasfireSession');
  if (sessionData) {
    try {
      const session = JSON.parse(sessionData);
      if (session.dealer && session.dealer.id) {
        dealerId = session.dealer.id;
      } else if (session.user && session.user.id) {
        dealerId = session.user.id;
      }
    } catch (error) {
      console.error('Oturum verisi ayrıştırılamadı:', error);
    }
  }
  
  // Form state'leri için normal input'lar
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [fireClass, setFireClass] = useState('a-wooden-stack');
  const [safetyFactor, setSafetyFactor] = useState('1.3');

  // localStorage anahtarları
  const FORM_DATA_KEY = 'alasfire_form_data';
  const CALCULATION_RESULTS_KEY = 'alasfire_calculation_results';
  
  // Proje bilgileri için otomatik tamamlama hook'ları
  const projectName = useFieldSuggestions('project_name', '', dealerId);
  const regionName = useFieldSuggestions('region_name', '', dealerId);  
  const companyName = useFieldSuggestions('company_name', '', dealerId);
  const roomName = useFieldSuggestions('room_name', '', dealerId);
  
  // Validation state'leri
  const [lengthValid, setLengthValid] = useState(true);
  const [widthValid, setWidthValid] = useState(true);
  const [heightValid, setHeightValid] = useState(true);
  const [fireClassValid, setFireClassValid] = useState(true);
  const [safetyFactorValid, setSafetyFactorValid] = useState(true);
  
  // Ürün seçimi state'leri
  const [selectedAlgorithm, setSelectedAlgorithm] = useState('greedy');
  const [validationMessage, setValidationMessage] = useState(null);
  
  // Manuel ürün seçimi için state değişkenleri
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [manualSelections, setManualSelections] = useState([]);
  
  // Hesaplama öncesi manuel ürün seçimi için state değişkenleri
  const [preCalculationSelections, setPreCalculationSelections] = useState([]);
  const [preCalculationProductId, setPreCalculationProductId] = useState('');
  const [preCalculationQuantity, setPreCalculationQuantity] = useState(1);
  
  // Ürün seçimi için state
  const [availableProducts, setAvailableProducts] = useState([]);
  const [calculationResults, setCalculationResults] = useState(null);
  
  // İşlem state'i
  const [isLoading, setIsLoading] = useState(false);

  // Form verilerini localStorage'a kaydetme
  const saveFormDataToStorage = () => {
    try {
      const formData = {
        length,
        width,
        height,
        fireClass,
        safetyFactor,
        projectName: projectName.value,
        regionName: regionName.value,
        companyName: companyName.value,
        roomName: roomName.value,
        selectedAlgorithm,
        preCalculationSelections,
        timestamp: Date.now()
      };
      localStorage.setItem(FORM_DATA_KEY, JSON.stringify(formData));
    } catch (error) {
      console.error('Form verileri kaydedilirken hata:', error);
    }
  };

  // localStorage'dan form verilerini yükleme
  const loadFormDataFromStorage = () => {
    try {
      const storedData = localStorage.getItem(FORM_DATA_KEY);
      if (storedData) {
        const formData = JSON.parse(storedData);
        // 24 saat içinde kaydedilmiş veriler varsa yükle
        const isRecent = formData.timestamp && (Date.now() - formData.timestamp < 24 * 60 * 60 * 1000);
        
        if (isRecent) {
          setLength(formData.length || '');
          setWidth(formData.width || '');
          setHeight(formData.height || '');
          setFireClass(formData.fireClass || 'a-wooden-stack');
          setSafetyFactor(formData.safetyFactor || '1.3');
    
          setSelectedAlgorithm(formData.selectedAlgorithm || 'greedy');
          setPreCalculationSelections(formData.preCalculationSelections || []);
          
          // Proje bilgilerini yükle
          if (formData.projectName) projectName.selectSuggestion(formData.projectName);
          if (formData.regionName) regionName.selectSuggestion(formData.regionName);
          if (formData.companyName) companyName.selectSuggestion(formData.companyName);
          if (formData.roomName) roomName.selectSuggestion(formData.roomName);
        }
      }
    } catch (error) {
      console.error('Form verileri yüklenirken hata:', error);
    }
  };

  // Hesaplama sonuçlarını localStorage'a kaydetme
  const saveCalculationResultsToStorage = (results) => {
    try {
      const dataToSave = {
        ...results,
        timestamp: Date.now()
      };
      localStorage.setItem(CALCULATION_RESULTS_KEY, JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Hesaplama sonuçları kaydedilirken hata:', error);
    }
  };

  // localStorage'dan hesaplama sonuçlarını yükleme
  const loadCalculationResultsFromStorage = () => {
    try {
      const storedResults = localStorage.getItem(CALCULATION_RESULTS_KEY);
      if (storedResults) {
        const results = JSON.parse(storedResults);
        // 24 saat içinde kaydedilmiş sonuçlar varsa yükle
        const isRecent = results.timestamp && (Date.now() - results.timestamp < 24 * 60 * 60 * 1000);
        
        if (isRecent) {
          setCalculationResults(results);
          if (onCalculationResults) {
            onCalculationResults(results);
          }
        }
      }
    } catch (error) {
      console.error('Hesaplama sonuçları yüklenirken hata:', error);
    }
  };

  // Sayfa yüklendiğinde form verilerini ve hesaplama sonuçlarını yükle
  useEffect(() => {
    loadFormDataFromStorage();
    loadCalculationResultsFromStorage();
  }, []);

  // Form verilerinde değişiklik olduğunda localStorage'a kaydet
  useEffect(() => {
    if (length || width || height || projectName.value || regionName.value || companyName.value || roomName.value) {
      saveFormDataToStorage();
    }
  }, [length, width, height, fireClass, safetyFactor, projectName.value, regionName.value, companyName.value, roomName.value, selectedAlgorithm, preCalculationSelections]);

  // Temizle butonu sadece form alanlarını ve hesaplama sonucunu temizler. Kayıtlı odalar/geçmiş silinmez.
  const handleClearAll = (event) => {
    event.preventDefault();
    setLength('');
    setWidth('');
    setHeight('');
    setFireClass('a-wooden-stack');
    setSafetyFactor('1.3');
    
    // Otomatik tamamlama alanlarını temizle
    projectName.selectSuggestion('');
    regionName.selectSuggestion('');
    companyName.selectSuggestion('');
    roomName.selectSuggestion('');
    
    // Validation state'lerini sıfırla
    setLengthValid(true);
    setWidthValid(true);
    setHeightValid(true);
    setFireClassValid(true);
    setSafetyFactorValid(true);
    
    // Ürün seçimi state'lerini sıfırla
    setSelectedProductId('');
    setSelectedQuantity(1);
    setAvailableProducts([]);
    setCalculationResults(null);
    setSelectedAlgorithm('greedy');
    setValidationMessage(null);
    
    // Mevcut ürün seçimlerini sıfırla
    setPreCalculationSelections([]);
    setPreCalculationProductId('');
    setPreCalculationQuantity(1);
    
    // Ana hesaplama sonuçlarını da temizle
    if (onCalculationResults) onCalculationResults(null);
    if (onError) onError(null);
    
    // Sadece form ve hesaplama sonucu ile ilgili localStorage anahtarlarını temizle
    try {
      localStorage.removeItem(FORM_DATA_KEY);
      localStorage.removeItem(CALCULATION_RESULTS_KEY);
    } catch (error) {
      console.error('localStorage temizlenirken hata:', error);
    }
    
    // Temizleme bildirimini göster (güncellenmiş çeviri kullanılıyor)
    toast(t('calculator.notifications.calculation_cleared'), {
      id: 'calculation-cleared',
      icon: '✅',
      style: {
        backgroundColor: '#D4EDDA',
        color: '#155724',
        border: '1px solid #C3E6CB'
      }
    });
  };

  // Ürün ekleme işlevi (hesaplama öncesi)
  const handleAddPreCalculationProduct = () => {
    if (!preCalculationProductId) {
      setValidationMessage(t('calculator.errors.select_product'));
      return;
    }
    
    if (preCalculationQuantity <= 0) {
      setValidationMessage(t('calculator.errors.invalid_quantity'));
      return;
    }
    
    // Ürün zaten seçilmiş mi kontrol et
    const existingProductIndex = preCalculationSelections.findIndex(
      (selection) => selection.productId === preCalculationProductId
    );
    
    const productData = getProductById(preCalculationProductId);
    
    if (existingProductIndex >= 0) {
      // Ürün zaten seçilmişse sayısını güncelle
      const updatedSelections = [...preCalculationSelections];
      updatedSelections[existingProductIndex].count += preCalculationQuantity;
      
      setPreCalculationSelections(updatedSelections);
      toast(t('calculator.notifications.product_updated'), {
        id: 'product-updated',
        icon: '✅',
        style: {
          backgroundColor: '#D4EDDA',
          color: '#155724',
          border: '1px solid #C3E6CB'
        }
      });
    } else {
      // Yeni ürün ekleniyor
      // Maksimum 3 farklı model kontrol et
      if (preCalculationSelections.length >= 3) {
        setValidationMessage(t('calculator.errors.max_product_types'));
        return;
      }
      
      setPreCalculationSelections([
        ...preCalculationSelections,
        {
          productId: preCalculationProductId,
          name: getProductName(productData, i18n.language),
          count: preCalculationQuantity,
          product: productData
        }
      ]);
      
      toast(t('calculator.notifications.product_added'), {
        id: 'product-added',
        icon: '✅',
        style: {
          backgroundColor: '#D4EDDA',
          color: '#155724',
          border: '1px solid #C3E6CB'
        }
      });
    }
    
    // Alanları temizle
    setPreCalculationProductId('');
    setPreCalculationQuantity(1);
    setValidationMessage(null);
  };

  // Ürün kaldırma işlemi (hesaplama öncesi)
  const handleRemovePreCalculationProduct = (productId) => {
    setPreCalculationSelections(
      preCalculationSelections.filter((selection) => selection.productId !== productId)
    );
    
    toast(t('calculator.notifications.product_removed'), {
      id: 'product-removed',
      icon: '✅',
      style: {
        backgroundColor: '#D4EDDA',
        color: '#155724',
        border: '1px solid #C3E6CB'
      }
    });
  };

  // Form doğrulama ve hesaplama
  const handleValidate = (event) => {
    event.preventDefault();
    
    // Önce varsa hata mesajını temizle
    setValidationMessage(null);
    if (onError) onError(null);
    
    let formIsValid = true;
    
    // Uzunluk kontrolü
    if (!length || isNaN(Number(length)) || Number(length) <= 0) {
      formIsValid = false;
      setLengthValid(false);
    } else {
      setLengthValid(true);
    }
    
    // Genişlik kontrolü
    if (!width || isNaN(Number(width)) || Number(width) <= 0) {
      formIsValid = false;
      setWidthValid(false);
    } else {
      setWidthValid(true);
    }
    
    // Yükseklik kontrolü
    if (!height || isNaN(Number(height)) || Number(height) <= 0) {
      formIsValid = false;
      setHeightValid(false);
    } else {
      setHeightValid(true);
    }
    
    // Yangın sınıfı kontrolü
    if (!fireClass) {
      formIsValid = false;
      setFireClassValid(false);
    } else {
      setFireClassValid(true);
    }
    
    // Emniyet faktörü kontrolü
    if (!safetyFactor || isNaN(Number(safetyFactor)) || Number(safetyFactor) < 1) {
      formIsValid = false;
      setSafetyFactorValid(false);
    } else {
      setSafetyFactorValid(true);
    }
    
    // Form geçerliyse
    if (formIsValid) {
      // Her durumda handleCalculate çağır
      handleCalculate();
    } else {
      // Form geçerli değilse, kullanıcının hataları görmesi için sayfayı yukarı kaydır
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Hesaplama işlemi
  const handleCalculate = async () => {
    setIsLoading(true);
    
    if (onError) onError(null);
    
    try {
      // Form verilerini sayılara dönüştür
      const calculationData = {
        length: parseFloat(length),
        width: parseFloat(width),
        height: parseFloat(height),
        fire_class: fireClass,
        safety_factor: parseFloat(safetyFactor),
        project_name: projectName.value,
        region_name: regionName.value,
        company_name: companyName.value,
        room_name: roomName.value
      };
      
      // Hacim hesapla (23m düzeltme mekanizması kaldırıldı)
      const roomVolume = calculationData.length * calculationData.width * calculationData.height;
      const adjustedVolume = roomVolume * calculationData.safety_factor;
      const fireDensity = getDensityByCode(calculationData.fire_class);
      const requiredMass = adjustedVolume * fireDensity;
      
      // Token kontrolü
      const sessionData = localStorage.getItem('alasfireSession');
      let token = null;
      
      if (sessionData) {
        try {
          const session = JSON.parse(sessionData);
          token = session.token;
        } catch (error) {
          // Oturum verisi bozuk, token null kalacak
        }
      }
      
      // Token yoksa hata göster
      if (!token) {
        if (onError) onError(t('calculator.errors.unauthorized'));
        setIsLoading(false);
        return;
      }

      // Dinamik algoritma ile hesaplama yap
      try {
        // Mevcut ürünler listesi
        const availableProducts = productData.map(product => ({
          id: product.id,
          name: getProductName(product, i18n.language),
          effectiveMass: product.effective_mass,
          minHeight: product.min_height,
          maxHeight: product.max_height,
          ...(product.isController && { isController: true }),
          count: 0
        }));
        
        // ALAS ALGORİTMASI - Hibrit çözümler üreten, çeşitli ürün kombinasyonları öneren algoritma
        const newAlgorithmResults = calculateWithAlasAlgorithm({
          length: calculationData.length,
          width: calculationData.width,
          height: calculationData.height,
          fireClass: calculationData.fire_class,
          safetyFactor: calculationData.safety_factor
        });
        
        // ESKİ KARMAŞIK EXCEL ALGORİTMASI KALDIRILDI - Sadece güvenli yeni algoritma kullanılıyor
        
        // Yeni algoritma sonuçlarını ana format olarak kullan
        const calculationResults = {
          room: {
            length: calculationData.length,
            width: calculationData.width,
            height: calculationData.height,
            volume: newAlgorithmResults.roomData.volume,
            adjusted_volume: newAlgorithmResults.roomData.adjustedVolume,
            fire_class: calculationData.fire_class,
            safety_factor: calculationData.safety_factor,
            required_mass: newAlgorithmResults.calculation.requiredMass
          },
          availableProducts: availableProducts,
          selections: {
            // Yeni Alas Algoritması sonuçları
            new_dynamic_safe: {
              algorithm: 'new_dynamic_safe',
              products: [...newAlgorithmResults.products, ...newAlgorithmResults.controllers],
              totalCount: newAlgorithmResults.calculation.totalUnits,
              totalEffectiveMass: newAlgorithmResults.calculation.totalMass,
              totalClassACapacity: newAlgorithmResults.products
                .reduce((sum, p) => sum + ((p.class_a_capacity || 0) * p.quantity), 0),
              totalClassBCapacity: newAlgorithmResults.products
                .reduce((sum, p) => sum + ((p.class_b_capacity || 0) * p.quantity), 0),
              verificationRatio: parseFloat(newAlgorithmResults.calculation.verificationRatio),
              optimizationRank: 1,
              solutionType: 'optimal'
            }
          },
          // Varsayılan seçim: Yeni Alas Algoritması
          appliedSelection: {
            algorithm: 'new_dynamic_safe',
            results: {
              selectedProducts: [...newAlgorithmResults.products, ...newAlgorithmResults.controllers],
              totalCount: newAlgorithmResults.calculation.totalUnits,
              totalEffectiveMass: newAlgorithmResults.calculation.totalMass,
              verificationRatio: parseFloat(newAlgorithmResults.calculation.verificationRatio)
            }
          },
          project_name: calculationData.project_name,
          region_name: calculationData.region_name,
          company_name: calculationData.company_name,
          room_name: calculationData.room_name
        };
        
        // Doğrulama oranı kontrolü - Yeni algoritma sonuçlarını kullan
        const verificationRatio = parseFloat(newAlgorithmResults.calculation.verificationRatio);
      
      // Doğrulama oranı durumunu değerlendir
      let verificationRatioResult = { status: 'success', message: 'verification_ratio_optimal' };
      
      if (verificationRatio < 100) {
        verificationRatioResult = { status: 'insufficient', message: 'verification_ratio_too_low' };
      } else if (verificationRatio > 150) {
        verificationRatioResult = { status: 'warning', message: 'verification_ratio_too_high' };
      } else if (verificationRatio >= 124 && verificationRatio <= 144) {
        verificationRatioResult = { status: 'success', message: 'verification_ratio_optimal' };
      }
      
      // Doğrulama oranı bildirimlerini göster
      if (verificationRatioResult.status === 'insufficient') {
        if (onError) onError(t('calculator.errors.verification_ratio_too_low'));
        toast(t(`calculator.notifications.${verificationRatioResult.message}`), {
          id: 'verification-ratio-insufficient',
          duration: 5000,
          icon: '❌',
          style: {
            backgroundColor: '#F8D7DA',
            color: '#721C24',
            border: '1px solid #F5C6CB'
          }
        });
      } else if (verificationRatioResult.status === 'warning') {
        toast(t(`calculator.notifications.${verificationRatioResult.message}`), {
          id: 'verification-ratio-too-high',
          duration: 3000,
          icon: '⚠️',
          style: {
            backgroundColor: '#FFF3CD',
            color: '#856404',
            border: '1px solid #FFEEBA'
          }
        });
      } else if (verificationRatioResult.status === 'success') {
        toast(t(`calculator.notifications.${verificationRatioResult.message}`), {
          id: 'verification-ratio-ideal',
          duration: 3000,
          icon: '✅',
          style: {
            backgroundColor: '#D4EDDA',
            color: '#155724',
            border: '1px solid #C3E6CB'
          }
        });
      }
      
      // Önce state'i güncelle, sonra üst bileşene ilet - bu sıralama önemli
      setCalculationResults(calculationResults);
      setAvailableProducts(calculationResults.availableProducts || []);
      
      // Manuel seçim için ilk ürünü seç
      if (calculationResults.availableProducts && calculationResults.availableProducts.length > 0) {
        setSelectedProductId(calculationResults.availableProducts[0].id.toString());
      }
      
      // Hesaplama sonuçlarını localStorage'a kaydet
      saveCalculationResultsToStorage(calculationResults);
      
      // Hesaplama sonuçlarını üst bileşene ilet - React'in state güncellemelerini garantilemek için setTimeout kullan
      setTimeout(() => {
        if (onCalculationResults) {
          onCalculationResults({
            success: true,
            data: calculationResults
          });
        }
        
        // İşlem tamamlandı
        setIsLoading(false);
      }, 0);
      
      // Hesaplama başarılı ise otomatik tamamlama önerilerini kaydet
      if (projectName && typeof projectName.saveSuggestion === 'function') {
        projectName.saveSuggestion(projectName.value);
      }
      if (regionName && typeof regionName.saveSuggestion === 'function') {
        regionName.saveSuggestion(regionName.value);
      }
      if (companyName && typeof companyName.saveSuggestion === 'function') {
        companyName.saveSuggestion(companyName.value);
      }
      if (roomName && typeof roomName.saveSuggestion === 'function') {
        roomName.saveSuggestion(roomName.value);
      }
      
    } catch (algorithmError) {
      if (onError) onError(algorithmError.message || t('calculator.errors.calculation_error'));
      
      toast(t('calculator.notifications.calculation_error'), {
        id: 'calculation-error',
        duration: 5000,
        icon: '❌',
        style: {
          backgroundColor: '#F8D7DA',
          color: '#721C24',
          border: '1px solid #F5C6CB'
        }
      });
      
      console.error('Algoritma hatası:', algorithmError);
      setIsLoading(false);
    }
    } catch (error) {
      if (onError) onError(t('calculator.errors.calculation_error'));
      
      toast(t('calculator.notifications.calculation_error'), {
        id: 'calculation-error',
        duration: 5000,
        icon: '❌',
        style: {
          backgroundColor: '#F8D7DA',
          color: '#721C24',
          border: '1px solid #F5C6CB'
        }
      });
      
      console.error('Hesaplama hatası:', error);
      setIsLoading(false);
    }
  };

  // API yanıtındaki ürün verilerini merkezi verilerle güncelleme fonksiyonu
  const updateProductDataFromCentralSource = (data) => {
    if (!data) return data;

    // Güncellenmiş veri kopya oluştur (veriyi değiştirmemek için)
    const updatedData = { ...data };

    // Mevcut ürünler listesini güncelle
    if (updatedData.availableProducts) {
      updatedData.availableProducts = productData.map(product => ({
        id: product.id,
        name: getProductName(product, i18n.language),
        effectiveMass: product.effective_mass,
        minHeight: product.min_height,
        maxHeight: product.max_height,
        ...(product.isController && { isController: true })
      }));
    }

    // Algoritma sonuçlarındaki ürünleri güncelle
    if (updatedData.selections) {
      // Her bir algoritma için ürün verilerini güncelle
      Object.keys(updatedData.selections).forEach(algorithm => {
        const selection = updatedData.selections[algorithm];
        
        if (selection && selection.products) {
          // Ürünleri güncelle
          selection.products = selection.products.map(product => {
            // Ürün ID'sine göre merkezi veri kaynağından ürünü bul
            const centralProduct = getProductById(product.id);
            
            // Merkezi üründen bilgi bulunamadıysa orijinal ürünü kullan
            if (!centralProduct) return product;
            
            // Mevcut ürün bilgilerini koruyarak, eksik verileri merkezi kaynaktan tamamla
            return {
              ...product,
              name: getProductName(centralProduct, i18n.language),
              effective_mass: centralProduct.effective_mass,
              mass: centralProduct.mass || product.mass,
              min_height: centralProduct.min_height,
              max_height: centralProduct.max_height,
              ...(centralProduct.isController && { isController: true })
            };
          });
        }
      });
    }

    // Manuel seçim sonuçlarını güncelle
    if (updatedData.appliedSelection && updatedData.appliedSelection.algorithm === 'manual' && 
        updatedData.appliedSelection.results && updatedData.appliedSelection.results.selectedProducts) {
      
      updatedData.appliedSelection.results.selectedProducts = updatedData.appliedSelection.results.selectedProducts.map(product => {
        const centralProduct = getProductById(product.id);
        if (!centralProduct) return product;
        
        return {
          ...product,
          name: getProductName(centralProduct, i18n.language),
          effective_mass: centralProduct.effective_mass,
          mass: centralProduct.mass || product.mass,
          min_height: centralProduct.min_height,
          max_height: centralProduct.max_height,
          ...(centralProduct.isController && { isController: true })
        };
      });
    }

    return updatedData;
  };

  return (
    <Card className="w-full border-muted shadow-sm">
      <CardHeader className="bg-muted/30 pb-3 px-3 sm:px-6">
        <div className="flex items-center gap-2">
          <CalculatorIcon className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
          <CardTitle className="text-lg sm:text-xl">{t('calculator.form_title')}</CardTitle>
        </div>
        <CardDescription className="text-sm sm:text-base">{t('calculator.subtitle')}</CardDescription>
      </CardHeader>
      
      <CardContent className="pt-4 sm:pt-5 px-3 sm:px-6">
        <form onSubmit={handleValidate}>
        {/* Proje Bilgileri */}
          <div className="mb-6 sm:mb-8 space-y-3 sm:space-y-4">
            <div className="flex items-center gap-2 text-base sm:text-lg font-medium text-foreground">
              <Building className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
              <h3>{t('calculator.project_info')}</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              <div className="space-y-2">
                <Label htmlFor="projectName" className="text-sm font-medium">
                  {t('calculator.project_name')}
                </Label>
                <AutocompleteInput
                  id="projectName"
                  value={projectName.value}
                  onChange={projectName.onChange}
                  placeholder={t('calculator.project_name_placeholder')}
                  className="border-input/80"
                  suggestions={projectName.suggestions}
                  showSuggestions={projectName.showSuggestions && projectName.suggestions.length > 0}
                  onSuggestionSelect={projectName.selectSuggestion}
                  onBlur={() => projectName.setShowSuggestions(false)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="regionName" className="text-sm font-medium">
                  {t('calculator.region_name')}
                </Label>
                <AutocompleteInput
                  id="regionName"
                  value={regionName.value}
                  onChange={regionName.onChange}
                  placeholder={t('calculator.region_name_placeholder')}
                  className="border-input/80"
                  suggestions={regionName.suggestions}
                  showSuggestions={regionName.showSuggestions && regionName.suggestions.length > 0}
                  onSuggestionSelect={regionName.selectSuggestion}
                  onBlur={() => regionName.setShowSuggestions(false)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="companyName" className="text-sm font-medium">
                  {t('calculator.company_name')}
                </Label>
                <AutocompleteInput
                  id="companyName"
                  value={companyName.value}
                  onChange={companyName.onChange}
                  placeholder={t('calculator.company_name_placeholder')}
                  className="border-input/80"
                  suggestions={companyName.suggestions}
                  showSuggestions={companyName.showSuggestions && companyName.suggestions.length > 0}
                  onSuggestionSelect={companyName.selectSuggestion}
                  onBlur={() => companyName.setShowSuggestions(false)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="roomName" className="text-sm font-medium">
                  {t('calculator.room_name')}
                </Label>
                <AutocompleteInput
                  id="roomName"
                  value={roomName.value}
                  onChange={roomName.onChange}
                  placeholder={t('calculator.room_name_placeholder')}
                  className="border-input/80"
                  suggestions={roomName.suggestions}
                  showSuggestions={roomName.showSuggestions && roomName.suggestions.length > 0}
                  onSuggestionSelect={roomName.selectSuggestion}
                  onBlur={() => roomName.setShowSuggestions(false)}
                />
              </div>
            </div>
          </div>
          
          <Separator className="my-4 sm:my-6" />
          
          {/* Hesaplama Parametreleri */}
          <div className="mb-6 sm:mb-8 space-y-4 sm:space-y-6">
            <div className="flex items-center gap-2 text-base sm:text-lg font-medium text-foreground">
              <Layers className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
              <h3>{t('calculator.calculation_params')}</h3>
            </div>
            
            {/* Oda Boyutları */}
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center gap-2 text-sm sm:text-base font-medium">
                <Ruler className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary/70 flex-shrink-0" />
                <h4>{t('calculator.dimensions')}</h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <MoveHorizontal className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground flex-shrink-0" />
                    <Label htmlFor="length" className="text-xs sm:text-sm font-medium truncate">
                      {t('calculator.length')} (m)
                    </Label>
                  </div>
                  <Input
                    id="length"
                    type="number"
                    step="0.01"
                    min="0.1"
                    value={length}
                    onChange={(e) => setLength(e.target.value)}
                    placeholder={t('calculator.length_placeholder')}
                    className={cn("border-input/80", !lengthValid && "border-destructive")}
                  />
                  {!lengthValid && (
                    <p className="text-destructive text-xs mt-1">{t('calculator.errors.positive_number')}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <MoveHorizontal className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground flex-shrink-0" />
                    <Label htmlFor="width" className="text-xs sm:text-sm font-medium truncate">
                      {t('calculator.width')} (m)
                    </Label>
                  </div>
                  <Input
                    id="width"
                    type="number"
                    step="0.01"
                    min="0.1"
                    value={width}
                    onChange={(e) => setWidth(e.target.value)}
                    placeholder={t('calculator.width_placeholder')}
                    className={cn("border-input/80", !widthValid && "border-destructive")}
                  />
                  {!widthValid && (
                    <p className="text-destructive text-xs mt-1">{t('calculator.errors.positive_number')}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <MoveVertical className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground flex-shrink-0" />
                    <Label htmlFor="height" className="text-xs sm:text-sm font-medium truncate">
                      {t('calculator.height')} (m)
                    </Label>
                  </div>
                  <Input
                    id="height"
                    type="number"
                    step="0.01"
                    min="0.1"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    placeholder={t('calculator.height_placeholder')}
                    className={cn("border-input/80", !heightValid && "border-destructive")}
                  />
                  {!heightValid && (
                    <p className="text-destructive text-xs mt-1">{t('calculator.errors.positive_number')}</p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Yangın Sınıfı */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm sm:text-base font-medium">
                <Flame className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary/70 flex-shrink-0" />
                <h4>{t('calculator.fire_class')}</h4>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">{t('calculator.fire_class_description')}</p>
              <div className="grid grid-cols-1 gap-2">
                <Label htmlFor="fireClass" className="flex items-center gap-1.5">
                  <Flame className="h-4 w-4 text-muted-foreground" />
                  {t('calculator.fire_class')}
                </Label>
                <Select 
                  onValueChange={(value) => setFireClass(value)} 
                  defaultValue={fireClass}
                  disabled={isLoading}
                  value={fireClass}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t('calculator.fire_class')} />
                  </SelectTrigger>
                  <SelectContent>
                    {getAllParameters().map(parameter => (
                      <SelectItem key={parameter.code} value={parameter.code}>
                        {i18n.language === 'en' && parameter.material_en 
                          ? `${parameter.fire_class} - ${parameter.material_en}` 
                          : t(`calculator.fire_class_options.${parameter.code}`) || `${parameter.fire_class} - ${parameter.material}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t('calculator.fire_class_description')}
                </p>
              </div>
            </div>
          
          {/* Emniyet Faktörü */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm sm:text-base font-medium">
                <Settings className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary/70 flex-shrink-0" />
                <h4>{t('calculator.safety_factor')}</h4>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">{t('calculator.safety_factor_description')}</p>
              <div className="max-w-xs space-y-2">
                <div className="flex items-center gap-1.5">
                  <RefreshCw className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground flex-shrink-0" />
                  <Label htmlFor="safety_factor" className="text-xs sm:text-sm font-medium">
                    {t('calculator.safety_factor')}
                  </Label>
                </div>
                <Input
                  id="safety_factor"
                  type="number"
                  step="0.1"
                  min="1.0"
                  value={safetyFactor}
                  onChange={(e) => setSafetyFactor(e.target.value)}
                  placeholder={t('calculator.safety_factor_placeholder')}
                  className={cn("border-input/80", !safetyFactorValid && "border-destructive")}
                />
                {!safetyFactorValid && (
                  <p className="text-destructive text-xs mt-1">{t('calculator.errors.min_safety_factor')}</p>
                )}
              </div>
            </div>
          </div>
          

            
          {/* Butonlar */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-end pt-2">

            <Button
              type="button"
              variant="outline"
              onClick={handleClearAll}
              className="gap-2 text-sm sm:text-base h-9 sm:h-10"
            >
              <RefreshCw className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              {t('common.clear')}
            </Button>
            <Button
              type="submit"
              className="gap-2 text-sm sm:text-base h-9 sm:h-10"
              disabled={isLoading}
            >
              <CalculatorIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              {isLoading ? t('calculator.calculating') : t('calculator.calculate_button')}
            </Button>
          </div>
        </form>
        

      </CardContent>
    </Card>
  );
};

export default CalculatorForm; 